import {
  Button,
  Input,
  Specialty,
  SpecialtySearchAutocomplete,
  getRegionOptions,
} from "@ews-admin/global-design-system";
import { Select } from "../../atoms/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  EditFacilityFormData,
  editFacilitySchema,
} from "../../../schemas/admin.schema";
import { useSpecialtyLabels } from "../../../hooks/useSpecialtyLabels";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  clearError,
  selectFacilitiesError,
  selectFacilitiesLoading,
  updateFacilityRequest,
} from "../../../store/reducers/facilitySlice";
import {
  fetchSpecialtiesRequest,
  selectSpecialties,
} from "../../../store/reducers/specialtySlice";
import {
  Facility,
  FacilityType,
  FacilityUpdate,
} from "../../../types/facility.definition";
import { AcceptedCountries } from "../../../types/user.definition";
import {
  countryLabel,
  facilityTypeLabel,
} from "../../../utils/enum-label-utils";
import { ConfirmationModal } from "../ConfirmationModal/ConfirmationModal";
import { Modal } from "../Modal/Modal";

// ── Types ──────────────────────────────────────────────────────────────────

interface EditFacilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  facility: Facility;
  onSuccess: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────

export const EditFacilityModal: React.FC<EditFacilityModalProps> = ({
  isOpen,
  onClose,
  facility,
  onSuccess,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectFacilitiesLoading);
  const error = useAppSelector(selectFacilitiesError);
  const storeSpecialties = useAppSelector(selectSpecialties);

  const [selectedSpecialties, setSelectedSpecialties] = useState<Specialty[]>(
    []
  );
  const [specialtiesDirty, setSpecialtiesDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // MED-1532: rebuilt on every render, this handed SpecialtySearchAutocomplete a
  // new `specialties` array identity each time a specialty was ticked, re-running
  // the control's `fetchSpecialties("")` effect and resetting the result list to
  // unfiltered while the search text was still in the box. Memoised, only a real
  // store change (a language switch, a refetch) resets it.
  const specialtiesForWidget: Specialty[] = useMemo(
    () =>
      storeSpecialties.map(dto => ({
        id: String(dto.id),
        code: dto.code,
        label: dto.label ?? "",
      })),
    [storeSpecialties]
  );

  // MED-1532: the specialties embedded in the facility payload are not
  // localized, so a reopened modal showed "Cardiology" on a French UI. Resolve
  // each chip's label against the language-aware specialty store — the same
  // helper the facility and doctor pages use — at render time rather than
  // freezing whatever label happened to be loaded when the modal opened.
  const specialtyLabel = useSpecialtyLabels();
  const selectedForWidget: Specialty[] = useMemo(
    () => selectedSpecialties.map(s => ({ ...s, label: specialtyLabel(s) })),
    [selectedSpecialties, specialtyLabel]
  );

  const submittedRef = useRef(false);
  const prevIsOpen = useRef(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors, isValid, isDirty },
  } = useForm<EditFacilityFormData>({
    resolver: zodResolver(editFacilitySchema(t)),
    mode: "onChange",
  });

  // Reset form only when the modal opens (false → true)
  useEffect(() => {
    if (!isOpen || prevIsOpen.current === isOpen) return;
    prevIsOpen.current = isOpen;
    submittedRef.current = false;
    dispatch(clearError());
    dispatch(fetchSpecialtiesRequest({ lang: i18n.language }));
    reset({
      name: facility.name ?? "",
      contactEmail: facility.contactEmail ?? "",
      phoneNumber: facility.phoneNumber ?? "",
      website: facility.website ?? "",
      facilityType: facility.facilityType,
      address: facility.location?.address ?? "",
      city: facility.location?.city ?? "",
      state: facility.location?.state ?? "",
      zip: facility.location?.zip ?? "",
      accessInformation: facility.location?.accessInformation ?? "",
      country:
        (facility.location?.country as AcceptedCountries) ??
        AcceptedCountries.SENEGAL,
      photoUrl: facility.photoUrl ?? "",
    });

    const initial: Specialty[] = (facility.specialties ?? [])
      .map(s => {
        if (typeof s === "string") return null;
        return s.id
          ? { id: String(s.id), code: s.code ?? "", label: s.label ?? "" }
          : null;
      })
      .filter((s): s is Specialty => s !== null);
    setSelectedSpecialties(initial);
    setSpecialtiesDirty(false);
  }, [isOpen, facility, dispatch, reset, i18n.language]);

  useEffect(() => {
    if (!isOpen) prevIsOpen.current = false;
  }, [isOpen]);

  const onSubmit = (data: EditFacilityFormData) => {
    const payload: FacilityUpdate = {};

    if (data.name !== facility.name) payload.name = data.name;
    if (data.contactEmail !== facility.contactEmail)
      payload.contactEmail = data.contactEmail;
    if (data.phoneNumber !== facility.phoneNumber)
      payload.phoneNumber = data.phoneNumber;
    if (data.website !== (facility.website ?? ""))
      payload.website = data.website || null;
    if (data.photoUrl !== (facility.photoUrl ?? ""))
      payload.photoUrl = data.photoUrl ?? "";
    if (data.facilityType !== facility.facilityType)
      payload.facilityType = data.facilityType;

    const locationChanged =
      data.address !== (facility.location?.address ?? "") ||
      data.city !== (facility.location?.city ?? "") ||
      data.state !== (facility.location?.state ?? "") ||
      data.zip !== (facility.location?.zip ?? "") ||
      data.accessInformation !== (facility.location?.accessInformation ?? "") ||
      data.country !==
        ((facility.location?.country as AcceptedCountries) ??
          AcceptedCountries.SENEGAL);

    if (locationChanged) {
      payload.location = {
        address: data.address,
        city: data.city,
        state: data.state,
        zip: data.zip,
        accessInformation: data.accessInformation || undefined,
        country: data.country,
      };
    }

    if (specialtiesDirty) {
      payload.specialtyIds = selectedSpecialties.map(s => Number(s.id));
    }

    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }

    // MED-1466: the parent unmounts this modal as soon as onClose runs, so the
    // outcome cannot be observed from the store here. The saga calls back, and
    // the save is confirmed with a toast like every other module.
    submittedRef.current = true;
    dispatch(
      updateFacilityRequest({
        facilityId: String(facility.id),
        data: payload,
        onSuccess: () => {
          toast.success(t("admin.facilities.editModal.success"));
          onSuccess();
        },
        onError: () => {
          toast.error(t("admin.facilities.editModal.error"));
        },
      })
    );

    onClose();
  };

  const isSaving = loading && submittedRef.current;
  const facilityTypes = Object.values(FacilityType);

  /**
   * MED-1543: Escape in the specialty list closed the whole modal and took
   * every edit with it, with nothing asked. Escape, the overlay and the X all
   * arrive here, and all three used to discard silently — so the guard belongs
   * on the close itself rather than on any one of them. An untouched form
   * still closes straight away: there is nothing to lose.
   */
  const hasUnsavedChanges = isDirty || specialtiesDirty;

  const handleCloseRequest = () => {
    if (isSaving) return;
    if (hasUnsavedChanges) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseRequest}
      title={t("admin.facilities.edit")}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6 space-y-4 overflow-y-auto max-h-[70vh]">
          {error && submittedRef.current && (
            <p className="text-sm text-red-600">
              {t("admin.facilities.editModal.error")}
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Input
                label={t("admin.facilities.editModal.name")}
                placeholder={t("admin.facilities.editModal.namePlaceholder")}
                required
                fullWidth
                error={errors.name?.message}
                {...register("name")}
              />
            </div>

            <Input
              label={t("admin.facilities.editModal.email")}
              placeholder={t("admin.facilities.editModal.emailPlaceholder")}
              type="email"
              required
              fullWidth
              error={errors.contactEmail?.message}
              {...register("contactEmail")}
            />

            <Input
              label={t("admin.facilities.editModal.phoneNumber")}
              placeholder={t(
                "admin.facilities.editModal.phoneNumberPlaceholder"
              )}
              type="tel"
              required
              fullWidth
              error={errors.phoneNumber?.message}
              {...register("phoneNumber")}
            />

            <div className="col-span-2">
              <Input
                label={t("admin.facilities.editModal.website")}
                placeholder={t("admin.facilities.editModal.websitePlaceholder")}
                fullWidth
                error={errors.website?.message}
                {...register("website")}
              />
            </div>

            <div className="col-span-2">
              <Input
                label={t("admin.facilities.editModal.photoUrl")}
                placeholder={t(
                  "admin.facilities.editModal.photoUrlPlaceholder"
                )}
                type="url"
                fullWidth
                error={errors.photoUrl?.message}
                {...register("photoUrl")}
              />
              {/* MED-1543: this block's caption was the field's own label
                  again, "URL de la photo", which says nothing about what is
                  under it. It is a preview, so it says so. */}
              {watch("photoUrl") && (
                <div className="flex gap-3 items-center mt-2">
                  <img
                    src={watch("photoUrl")}
                    alt={t("admin.facilities.editModal.photoPreview")}
                    className="object-cover w-16 h-16 rounded-lg border border-gray-200"
                    onError={e => {
                      (e.currentTarget as HTMLImageElement).style.display =
                        "none";
                    }}
                  />
                  <p className="text-xs text-gray-500">
                    {t("admin.facilities.editModal.photoPreview")}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              {t("admin.facilities.editModal.facilityType")}
            </label>
            <Controller
              name="facilityType"
              control={control}
              render={({ field }) => (
                <select
                  {...field}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {facilityTypes.map(type => (
                    <option key={type} value={type}>
                      {facilityTypeLabel(t, type)}
                    </option>
                  ))}
                </select>
              )}
            />
            {errors.facilityType && (
              <p className="mt-1 text-sm text-red-600">
                {errors.facilityType.message}
              </p>
            )}
          </div>

          <div>
            <p className="mb-3 text-sm font-medium text-gray-700">
              {t("admin.facilities.editModal.location")}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Input
                  label={t("admin.facilities.editModal.address")}
                  placeholder={t(
                    "admin.facilities.editModal.addressPlaceholder"
                  )}
                  fullWidth
                  {...register("address")}
                />
              </div>
              <Input
                label={t("admin.facilities.editModal.city")}
                placeholder={t("admin.facilities.editModal.cityPlaceholder")}
                fullWidth
                {...register("city")}
              />
              <Controller
                name="state"
                control={control}
                render={({ field }) => (
                  <Select
                    label={t("admin.facilities.editModal.state")}
                    options={getRegionOptions(watch("country"))}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.state?.message}
                  />
                )}
              />
              <Input
                label={t("admin.facilities.editModal.zip")}
                placeholder={t("admin.facilities.editModal.zipPlaceholder")}
                fullWidth
                {...register("zip")}
              />
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("admin.facilities.editModal.country")}
                </label>
                <Controller
                  name="country"
                  control={control}
                  render={({ field }) => (
                    <select
                      {...field}
                      className="px-3 py-2 w-full text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.values(AcceptedCountries).map(c => (
                        <option key={c} value={c}>
                          {countryLabel(t, c)}
                        </option>
                      ))}
                    </select>
                  )}
                />
              </div>
              <div className="col-span-2">
                <Input
                  label={t("admin.facilities.editModal.accessInformation")}
                  placeholder={t(
                    "admin.facilities.editModal.accessInformationPlaceholder"
                  )}
                  fullWidth
                  {...register("accessInformation")}
                />
              </div>
            </div>
          </div>
          {/* MED-1543: Escape inside the specialty list belongs to the list —
              it closes the dropdown. It used to carry on up to the dialog's
              own document-level handler, which closed the whole dialog and
              discarded every edit. */}
          <div
            onKeyDown={event => {
              if (event.key === "Escape") event.stopPropagation();
            }}
          >
            <SpecialtySearchAutocomplete
              specialties={specialtiesForWidget}
              selectedSpecialties={selectedForWidget}
              onSpecialtiesChange={items => {
                setSelectedSpecialties(items);
                setSpecialtiesDirty(true);
              }}
              title={t("admin.facilities.addModal.specialtiesTitle")}
              label={t("admin.facilities.addModal.specialtiesLabel")}
              placeholder={t(
                "admin.facilities.addModal.specialtiesPlaceholder"
              )}
              removeSpecialtyLabel={t(
                "admin.facilities.addModal.specialtyRemove"
              )}
              getSelectedCountText={count =>
                t("admin.facilities.addModal.specialtiesSelected", { count })
              }
            />
          </div>
        </div>

        <div className="flex gap-3 justify-end px-6 py-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={handleCloseRequest}
            disabled={isSaving}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={!isValid || (!isDirty && !specialtiesDirty) || isSaving}
          >
            {isSaving ? t("common.processing") : t("common.save")}
          </Button>
        </div>
      </form>

      <ConfirmationModal
        isOpen={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          onClose();
        }}
        title={t("admin.facilities.editModal.discardTitle")}
        message={t("admin.facilities.editModal.discardMessage")}
        confirmLabel={t("admin.facilities.editModal.discardConfirm")}
        cancelLabel={t("admin.facilities.editModal.discardCancel")}
        variant="warning"
      />
    </Modal>
  );
};
